import React, { useEffect, useMemo, useState } from "react"
import { getAPIBaseURL } from "../../services/helpers"
import { Link } from "react-router-dom"
import { getData } from "../../services/request"
import {
    BuildingOfficeIcon,
    MagnifyingGlassIcon,
    MapPinIcon,
    EnvelopeIcon,
    PhoneIcon
} from "@heroicons/react/24/outline"

const AdminCenters = () => {
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [query, setQuery] = useState("")

    const fetchItems = async () => {
        setLoading(true)
        setError("")
        // Assuming CENTER is the correct role identifier on backend. 
        // If it was HOSPITAL in legacy, logic elsewhere ensures new users are CENTER.
        // Querying for CENTER role users.
        const url = `${getAPIBaseURL()}/v1/users`
        const params = { role: 'CENTER', limit: 200, skip: 0 }

        try {
            const res = await getData(url, {}, params)
            setLoading(false)
            if (res.error === "") {
                if (res.result.status === 200) {
                    setItems(res.result.data?.data || [])
                } else {
                    setError(res.result.data?.message || 'Failed to load centers')
                }
            } else {
                setError(res.result?.data?.message || 'Network error')
            }
        } catch (err) {
            setLoading(false)
            setError('An unexpected error occurred')
        }
    }

    useEffect(() => { fetchItems() }, [])

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return items
        return items.filter(u => (
            (u.organization || '').toLowerCase().includes(q) ||
            (u.name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q) ||
            (u.phoneNumber || '').toLowerCase().includes(q)
        ))
    }, [items, query])

    return (
        <div className="max-w-7xl mx-auto">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-slate-900">Event Centers</h1>
                <p className="text-slate-500 mt-1">Manage registered event centers and partners</p>
            </div>

            <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between items-center">
                <div className="relative w-full max-w-md">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by name, email, or phone..."
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent bg-white shadow-sm"
                    />
                </div>
                <div className="flex gap-2">
                    <button onClick={fetchItems} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition">
                        Refresh List
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-20">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
                    <p className="mt-2 text-slate-500">Loading centers...</p>
                </div>
            ) : error ? (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-100 text-center">
                    {error}
                    <button onClick={fetchItems} className="block mx-auto mt-2 text-sm font-bold hover:underline">Try Again</button>
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-4 font-semibold text-slate-700">Organization / Name</th>
                                    <th className="px-6 py-4 font-semibold text-slate-700">Contact</th>
                                    <th className="px-6 py-4 font-semibold text-slate-700">Location</th>
                                    <th className="px-6 py-4 font-semibold text-slate-700">Status</th>
                                    <th className="px-6 py-4 font-semibold text-slate-700 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                                            No centers found matching your search.
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map(center => (
                                        <tr key={center._id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center text-cyan-600 font-bold text-lg">
                                                        {center.organization?.charAt(0) || center.name?.charAt(0) || 'C'}
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-slate-900">{center.organization || center.name || 'Unnamed Center'}</p>
                                                        <p className="text-xs text-slate-500">Joined {new Date(center.createdAt).toLocaleDateString()}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2 text-slate-600">
                                                        <EnvelopeIcon className="w-4 h-4 text-slate-400" />
                                                        {center.email}
                                                    </div>
                                                    <div className="flex items-center gap-2 text-slate-600">
                                                        <PhoneIcon className="w-4 h-4 text-slate-400" />
                                                        {center.phoneNumber || 'N/A'}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2 text-slate-600">
                                                    <MapPinIcon className="w-4 h-4 text-slate-400" />
                                                    {center.state ? `${center.state}, ${center.localGovernment || 'Nigeria'}` : 'Location not set'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${center.status === 1 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'
                                                    }`}>
                                                    {center.status === 1 ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <Link
                                                    to={`/admin/centers/${center._id}`}
                                                    className="text-cyan-600 hover:text-cyan-800 font-medium hover:underline"
                                                >
                                                    View Details
                                                </Link>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminCenters
